import FirestoreBaseService from './firestoreBase';
import { USER_ROLES } from '../context/AuthContext';

class InvitationService extends FirestoreBaseService {
  constructor() {
    super('invitations');
    this.mentorsService = new FirestoreBaseService('mentors');
  }

  /**
   * 新しい招待を作成
   * @param {string} email - 招待するメールアドレス
   * @param {string} inviterId - 招待者のUID
   * @param {Object} additionalData - 追加データ
   */
  async createInvitation(email, inviterId, additionalData = {}) {
    try {
      // OG重複確認
      const existingMentors = await this.mentorsService.getMany([
        { field: '連絡先', operator: '==', value: email }
      ]);

      if (existingMentors.length > 0) {
        throw new Error('このメールアドレスは既にOGとして登録されています。');
      }

      // 既存の招待確認
      const existingInvitations = await this.getMany([
        { field: 'email', operator: '==', value: email },
        { field: 'status', operator: 'in', value: ['pending', 'completed'] }
      ]);

      if (existingInvitations.length > 0) {
        throw new Error('このメールアドレスには既に招待が送信されています。');
      }

      // 招待コードを生成（6文字のランダム文字列）
      const inviteCode = this.generateInviteCode();

      // 招待データを作成
      const invitationData = {
        email,
        inviteCode,
        status: 'pending',
        role: USER_ROLES.MENTOR,
        inviterId,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7日後
        ...additionalData
      };

      const docRef = await this.add(invitationData);
      
      return {
        id: docRef.id,
        inviteCode,
        url: `${window.location.origin}/mentor-register?code=${inviteCode}`,
        ...invitationData
      };
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 招待を検証
   * @param {string} inviteCode - 招待コード
   */
  async validateInvitation(inviteCode) {
    try {
      const invitations = await this.getMany([
        { field: 'inviteCode', operator: '==', value: inviteCode },
        { field: 'status', operator: '==', value: 'pending' }
      ]);

      if (invitations.length === 0) {
        throw new Error('無効な招待コードです。');
      }

      const invitation = invitations[0];
      const now = new Date();
      
      // 期限チェック
      if (invitation.expiresAt && invitation.expiresAt.toDate() < now) {
        // 期限切れの招待を更新
        await this.update(invitation.id, { status: 'expired' });
        throw new Error('招待の有効期限が切れています。');
      }

      return invitation;
    } catch (error) {
      console.error('Error validating invitation:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 招待状態を更新
   * @param {string} invitationId - 招待ID
   * @param {string} status - 新しい状態
   * @param {Object} additionalData - 追加データ
   */
  async updateInvitationStatus(invitationId, status, additionalData = {}) {
    try {
      const updateData = {
        status,
        ...additionalData
      };

      if (status === 'completed') {
        updateData.completedAt = new Date();
      } else if (status === 'expired') {
        updateData.expiredAt = new Date();
      }

      await this.update(invitationId, updateData);
      return { success: true, invitationId, status };
    } catch (error) {
      console.error('Error updating invitation status:', error);
      throw this.handleError(error);
    }
  }

  /**
   * ユーザーの招待履歴を取得
   * @param {string} inviterId - 招待者のUID
   * @param {number} limitCount - 取得件数制限
   */
  async getInvitationsByUser(inviterId, limitCount = 50) {
    try {
      return await this.getMany(
        [{ field: 'inviterId', operator: '==', value: inviterId }],
        [{ field: 'createdAt', direction: 'desc' }],
        limitCount
      );
    } catch (error) {
      console.error('Error getting invitations by user:', error);
      throw this.handleError(error);
    }
  }

  /**
   * ユーザーの招待制限をチェック
   * @param {string} inviterId - 招待者のUID
   * @param {string} userRole - ユーザーロール
   */
  async checkInvitationLimit(inviterId, userRole) {
    try {
      // 管理者は無制限
      if (userRole === USER_ROLES.ADMIN) {
        return { canInvite: true, remaining: -1 };
      }

      // 一般ユーザーは3件まで
      const invitations = await this.getMany([
        { field: 'inviterId', operator: '==', value: inviterId }
      ]);

      const limit = 3;
      const used = invitations.length;
      const remaining = Math.max(0, limit - used);

      return {
        canInvite: remaining > 0,
        remaining,
        limit,
        used
      };
    } catch (error) {
      console.error('Error checking invitation limit:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 期限切れの招待をクリーンアップ
   */
  async cleanupExpiredInvitations() {
    try {
      const now = new Date();
      const expiredInvitations = await this.getMany([
        { field: 'status', operator: '==', value: 'pending' }
      ]);

      const expiredIds = expiredInvitations
        .filter(invitation => invitation.expiresAt && invitation.expiresAt.toDate() < now)
        .map(invitation => invitation.id);

      // バッチで期限切れ状態に更新
      if (expiredIds.length > 0) {
        const operations = expiredIds.map(id => ({
          type: 'update',
          docId: id,
          data: { 
            status: 'expired',
            expiredAt: now
          }
        }));

        await this.batchWrite(operations);
      }

      return { processed: expiredIds.length };
    } catch (error) {
      console.error('Error cleaning up expired invitations:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 招待コードを生成（6文字のランダム文字列）
   */
  generateInviteCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * 招待統計を取得
   * @param {string} inviterId - 招待者のUID（オプション）
   */
  async getInvitationStats(inviterId = null) {
    try {
      const conditions = inviterId 
        ? [{ field: 'inviterId', operator: '==', value: inviterId }]
        : [];

      const allInvitations = await this.getMany(conditions);

      const stats = {
        total: allInvitations.length,
        pending: 0,
        completed: 0,
        expired: 0
      };

      allInvitations.forEach(invitation => {
        stats[invitation.status] = (stats[invitation.status] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error getting invitation stats:', error);
      throw this.handleError(error);
    }
  }
}

// Create and export invitation service instance
const invitationService = new InvitationService();
export default invitationService;